import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { evalApiClient } from '@/lib/api/eval.client'
import { EvalPromptType, EVAL_PROMPT_TYPE_LABELS } from '@super/shared-types'
import type { LmRunLog } from '@super/shared-types'
import {
  EVAL_PROMPT_TEMPLATES,
  deriveEvalCaseFields,
  type EvalCaseFields,
} from '@super/eval-framework'
import {
  AssertionBuilder,
  serializeAssertions,
  hasCompleteAssertion,
  type AssertionRow,
} from '@/components/eval/AssertionBuilder'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  runLog: LmRunLog
  companyId: string
}

function defaultName(promptType: string, createdAt: string): string {
  const label =
    EVAL_PROMPT_TYPE_LABELS[promptType as EvalPromptType] ?? promptType
  const date = new Date(createdAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${label} – ${date}`
}

function initInputValues(
  runLog: LmRunLog,
  fields: EvalCaseFields,
): Record<string, string> {
  const inputCtx = (runLog.inputContext as Record<string, unknown>) ?? {}
  const values: Record<string, string> = {}
  for (const field of fields.inputFields) {
    if (field.target === 'content') {
      values[field.key] = runLog.content
    } else if (field.type === 'json-array') {
      values[field.key] =
        inputCtx[field.key] != null
          ? JSON.stringify(inputCtx[field.key], null, 2)
          : ''
    } else if (field.type === 'line-list') {
      const arr = inputCtx[field.key]
      values[field.key] = Array.isArray(arr) ? arr.join('\n') : ''
    } else {
      values[field.key] =
        inputCtx[field.key] != null ? String(inputCtx[field.key]) : ''
    }
  }
  return values
}

function initAssertions(): AssertionRow[] {
  return []
}

function OutputSection({ output }: { output: Record<string, unknown> }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-t pt-4">
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        Output
      </button>
      {open && (
        <pre className="mt-2 text-[11px] bg-muted border border-border rounded p-3 overflow-x-hidden whitespace-pre-wrap break-all text-foreground leading-relaxed">
          {JSON.stringify(output, null, 2)}
        </pre>
      )}
    </div>
  )
}

export function PromoteToEvalModal({
  open,
  onOpenChange,
  runLog,
  companyId,
}: Props) {
  const navigate = useNavigate()
  const promptType = runLog.promptType as EvalPromptType
  const template = EVAL_PROMPT_TEMPLATES[promptType]
  const fields = template
    ? deriveEvalCaseFields(template)
    : { inputFields: [], outputFields: [] }

  const [name, setName] = useState(() =>
    defaultName(runLog.promptType, runLog.createdAt),
  )
  const [description, setDescription] = useState('')
  const [scope, setScope] = useState<'company' | 'global'>('company')
  const [values, setValues] = useState<Record<string, string>>(() =>
    initInputValues(runLog, fields),
  )
  const [assertions, setAssertions] = useState<AssertionRow[]>(() =>
    initAssertions(),
  )
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function setValue(key: string, v: string) {
    setValues(prev => ({ ...prev, [key]: v }))
  }

  function reset() {
    setName(defaultName(runLog.promptType, runLog.createdAt))
    setDescription('')
    setScope('company')
    setValues(initInputValues(runLog, fields))
    setAssertions(initAssertions())
    setError(null)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  const isFormValid = name.trim().length > 0 && hasCompleteAssertion(assertions)

  async function handleSubmit() {
    setError(null)

    let content = ''
    const inputContext: Record<string, unknown> = {}

    for (const field of fields.inputFields) {
      const raw = values[field.key] ?? ''
      if (field.required && !raw.trim()) {
        setError(`${field.label} is required`)
        return
      }
      if (!raw.trim()) continue

      if (field.target === 'content') {
        content = raw
      } else if (field.type === 'json-array') {
        try {
          inputContext[field.key] = JSON.parse(raw)
        } catch {
          setError(`${field.label} must be valid JSON`)
          return
        }
      } else if (field.type === 'line-list') {
        inputContext[field.key] = raw
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)
      } else {
        inputContext[field.key] = raw
      }
    }

    const expectedOutput = serializeAssertions(assertions, fields.outputFields)
    if (Object.keys(expectedOutput).length === 0) {
      setError('At least one assertion is required')
      return
    }

    setIsSaving(true)
    const result = await evalApiClient.createCase({
      name: name.trim(),
      description: description.trim() || undefined,
      companyId: scope === 'company' ? companyId : undefined,
      promptType,
      content,
      inputContext:
        Object.keys(inputContext).length > 0 ? inputContext : undefined,
      expectedOutput,
      sourceSessionId: runLog.sessionId,
      sourceThreadId: runLog.threadId,
    })
    setIsSaving(false)

    if (result) {
      toast.success('Eval case created', {
        action: {
          label: 'View case',
          onClick: () => navigate(`/evaluations/cases/${result.id}`),
        },
      })
      onOpenChange(false)
    } else {
      setError('Failed to create eval case')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Promote to Eval Case</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* ── Meta ── */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="promote-name">Name *</Label>
              <Input
                id="promote-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Roosevelt Blvd exact match"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Prompt type</Label>
              <Input
                value={EVAL_PROMPT_TYPE_LABELS[promptType] ?? promptType}
                disabled
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="promote-description">Description</Label>
              <Input
                id="promote-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Scope</Label>
              <div className="flex rounded-md border overflow-hidden">
                <button
                  type="button"
                  className={`flex-1 px-3 py-2 text-sm transition-colors ${scope === 'company' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setScope('company')}
                >
                  This company
                </button>
                <button
                  type="button"
                  className={`flex-1 px-3 py-2 text-sm border-l transition-colors ${scope === 'global' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setScope('global')}
                >
                  Global
                </button>
              </div>
            </div>
          </div>

          {/* ── Input fields ── */}
          {fields.inputFields.map(field => {
            const isContent = field.target === 'content'
            return (
              <div key={field.key} className="flex flex-col gap-1.5">
                <Label htmlFor={`promote-in-${field.key}`}>
                  {field.label}
                  {field.required ? ' *' : ''}
                </Label>
                <Textarea
                  id={`promote-in-${field.key}`}
                  value={values[field.key] ?? ''}
                  onChange={e => setValue(field.key, e.target.value)}
                  readOnly={isContent}
                  rows={field.rows ?? 3}
                  className={
                    field.type === 'json-array'
                      ? 'font-mono text-xs'
                      : isContent
                        ? 'bg-muted text-muted-foreground resize-none'
                        : undefined
                  }
                />
              </div>
            )
          })}

          {/* ── LLM Output ── */}
          {runLog.actualOutput && (
            <OutputSection
              output={runLog.actualOutput as Record<string, unknown>}
            />
          )}

          {/* ── Assertions ── */}
          {fields.outputFields.length > 0 && (
            <div className="border-t pt-4 flex flex-col gap-3">
              <p className="text-sm font-medium text-muted-foreground">
                Assertions
              </p>
              <AssertionBuilder
                fields={fields.outputFields}
                value={assertions}
                onChange={setAssertions}
              />
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !isFormValid}>
            {isSaving ? 'Saving…' : 'Create case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
